import { Center, Icon, IconButton, Menu, Spinner, useDisclosure } from "@chakra-ui/react"
import { FiArchive, FiDownload, FiEdit, FiTrash } from "react-icons/fi"
import { Tooltip } from "../UI/tooltip"
import { MdOutlinePublishedWithChanges } from "react-icons/md"

import type {
  AuthorGroupPrivate,
  AuthorPreview,
  CategoryPrivate,
  FormsContactPreview,
  MediaItemPreview,
  PublicationPrivatePreview,
  RolePreview,
  SettingsMimeTypePrivate,
  SettingsRateChartPrivate,
  TagPreview,
  UserPrivate
} from "../../client"
import { lazy, Suspense } from "react"

const EditUser = lazy(() => import("../Admin/EditUser"))
const EditAuthor = lazy(() => import("../Authors/EditAuthor"))
const EditAuthorGroup = lazy(() => import("../AuthorGroups/EditAuthorGroup"))
const EditTag = lazy(() => import("../Tags/EditTag"))
const EditCategory = lazy(() => import("../Categories/EditCategory"))
const EditRole = lazy(() => import("../Roles/EditRole"))
const EditSettingsRatechart = lazy(() => import("../Ratecharts/EditSettingsRatechart"))
const EditPublication = lazy(() => import("../Publications/EditPublication"))
const Delete = lazy(() => import("./DeleteAlert"))
const Archive = lazy(() => import("./ArchiveAlert"))

interface ActionsMenuProps {
  type: string
  value: TagPreview |
  CategoryPrivate |
  UserPrivate |
  MediaItemPreview |
  SettingsRateChartPrivate |
  AuthorPreview |
  AuthorGroupPrivate |
  RolePreview |
  SettingsMimeTypePrivate |
  FormsContactPreview |
  PublicationPrivatePreview
  disableDelete?: boolean
  hideDelete?: boolean
  disableEdit?: boolean
  hideEdit?: boolean
  file_name?: string
  file_path?: string
  showArchive?: boolean
  is_archived?: boolean
}


const ActionsMenu = ({
  type,
  value,
  disableDelete,
  hideDelete,
  disableEdit,
  hideEdit,
  file_name,
  file_path,
  showArchive,
  is_archived,
}: ActionsMenuProps) => {
  const editModal = useDisclosure()
  const deleteModal = useDisclosure()
  const archiveModal = useDisclosure()

  const onDownload = () => {
    if(file_name != undefined && file_path != undefined) {
      const link = document.createElement("a")
      link.download = file_name
      link.href = file_path
      link.click()
    }
  }

  return (
    <Menu.Root>
      {file_name != null && file_path != null && (
        <Tooltip showArrow content={`${type} herunterladen`} openDelay={500}>
          <Menu.Trigger asChild>
            <IconButton
              onClick={onDownload}
              variant="outline"
              bg="main"
              color="light"
              m={1}
              _hover={{ bg: "tertiary" }}
              aria-label={`${type} herunterladen`}
            >
              <Icon as={FiDownload} boxSize="16px" />
            </IconButton>
          </Menu.Trigger>
        </Tooltip>
      )}
      {!hideEdit && (
        <Tooltip
          showArrow
          content={`${type} bearbeiten`}
          openDelay={500}
          disabled={disableEdit}
        >
          <Menu.Trigger asChild>
            <IconButton
              onClick={editModal.onOpen}
              disabled={disableEdit}
              variant="outline"
              bg="main"
              color="light"
              m={1}
              _hover={{ bg: "tertiary" }}
              aria-label={`${type} bearbeiten`}
            >
              <Icon as={FiEdit} boxSize="16px" />
            </IconButton>
          </Menu.Trigger>
        </Tooltip>
      )}
      {!hideEdit && editModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          {
            {
              Benutzer: (
                <EditUser
                  user={value as UserPrivate}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Tag: (
                <EditTag
                  tag={value as TagPreview}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Kategorie: (
                <EditCategory
                  category={value as CategoryPrivate}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Autor: (
                <EditAuthor
                  authorId={value.id}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Autorengruppe: (
                <EditAuthorGroup
                  authorgroup={value as AuthorGroupPrivate}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Rolle: (
                <EditRole
                  roleId={value.id}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Tariftabelle: (
                <EditSettingsRatechart
                  ratechart={value as SettingsRateChartPrivate}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              ),
              Publikation: (
                <EditPublication
                  publicationId={value.id}
                  isOpen={editModal.open}
                  onClose={editModal.onClose}
                />
              )
            }[type]
          }
        </Suspense>
      }
      {showArchive && (
        <>
          <Tooltip
            showArrow
            content={is_archived ? `${type} veröffentlichen` : `${type} archivieren`}
            openDelay={500}
          >
            <Menu.Trigger asChild>
              <IconButton
                onClick={archiveModal.onOpen}
                bg="dim"
                color="light"
                m={1}
                _hover={{ bg: "dimHover" }}
                aria-label={is_archived ? `${type} veröffentlichen` : `${type} archivieren`}
              >
                <Icon as={is_archived ? MdOutlinePublishedWithChanges : FiArchive} boxSize="16px" />
              </IconButton>
            </Menu.Trigger>
          </Tooltip>
          {archiveModal.open &&
            <Suspense fallback={
              <Center
                bg="rgba(0, 0, 0, 0.3)"
                position="fixed"
                top="0"
                left="0"
                width="100vw"
                height="100vh"
                zIndex="10000"
              >
                <Spinner size="lg" color="white" />
              </Center>}
            >
                <Archive
                  type={type}
                  id={value.id}
                  is_archived={is_archived}
                  isOpen={archiveModal.open}
                  onClose={archiveModal.onClose}
                />
            </Suspense>
          }
        </>
      )}
      {!hideDelete && (
        <>
          <Tooltip
            showArrow
            content={`${type} löschen`}
            openDelay={500}
            disabled={disableDelete}
          >
            <Menu.Trigger asChild>
              <IconButton
                onClick={deleteModal.onOpen}
                disabled={disableDelete}
                bg="danger"
                color="light"
                m={1}
                _hover={{ bg: "hoverDanger" }}
                aria-label={`${type} löschen`}
              >
                <Icon as={FiTrash} boxSize="16px" />
              </IconButton>
            </Menu.Trigger>
          </Tooltip>
          {deleteModal.open &&
            <Suspense fallback={
              <Center
                bg="rgba(0, 0, 0, 0.3)"
                position="fixed"
                top="0"
                left="0"
                width="100vw"
                height="100vh"
                zIndex="10000"
              >
                <Spinner size="lg" color="white" />
              </Center>}
            >
                <Delete
                  type={type}
                  id={value.id}
                  isOpen={deleteModal.open}
                  onClose={deleteModal.onClose}
                />
            </Suspense>
          }
        </>
      )}
    </Menu.Root>
  )
}

export default ActionsMenu
